"use client";
import React, { useState, useEffect, useRef } from "react";
// 1. Import motion and AnimatePresence for animations
import { motion, AnimatePresence } from "framer-motion";
import { io } from "socket.io-client";
// 2. Import icons for profile pictures
import { FaDownload, FaTimesCircle, FaUser, FaHeadset } from 'react-icons/fa';

// --- Socket & API Configuration (Unchanged) ---
const SOCKET_SERVER_URL = "https://voidapi.shigjeta.com/";
const CHAT_API_ENDPOINT = "https://voidapi.shigjeta.com/api/chat/ask";

// --- Multi-Lingual Configuration (Unchanged) ---
const languages = {
  en: {
    initialMessage: "Hello! I am Void, your virtual assistant. How can I help you today?",
    connecting: "👤 Connecting you to a live support specialist...",
    agentJoined: (name) => `${name} has joined the chat. You are now talking to a real person.`,
    chatEnded: "This conversation has ended.",
    chatEndedByAgent: "The support specialist has ended this conversation.",
    placeholder: "Type your message...",
    placeholderWaiting: "Please wait for a specialist...",
    placeholderEnded: "This chat has ended. You can download the transcript.",
  },
  sq: {
    initialMessage: "Përshëndetje! Unë jam Void, asistenti juaj virtual. Si mund t'ju ndihmoj sot?",
    connecting: "Duke ju lidhur me një specialist të mbështetjes direkte...",
    agentJoined: (name) => `${name} i është bashkuar bisedës. Tani po flisni me një person real.`,
    chatEnded: "Kjo bisedë ka përfunduar.",
    chatEndedByAgent: "Specialisti i mbështetjes e ka mbyllur këtë bisedë.",
    placeholder: "Shkruani mesazhin tuaj...",
    placeholderWaiting: "Ju lutem prisni për një specialist...",
    placeholderEnded: "Kjo bisedë ka përfunduar. Mund ta shkarkoni transkriptin.",
  },
  sr: {
    initialMessage: "Zdravo! Ja sam Void, vaš virtuelni asistent. Kako vam danas mogu pomoći?",
    connecting: "Povezujem vas sa specijalistom za podršku uživo...",
    agentJoined: (name) => `${name} se pridružio razgovoru. Sada razgovarate sa stvarnom osobom.`,
    chatEnded: "Ovaj razgovor je završen.",
    chatEndedByAgent: "Specijalista za podršku je završio ovaj razgovor.",
    placeholder: "Upišite vašu poruku...",
    placeholderWaiting: "Molimo sačekajte stručnjaka...",
    placeholderEnded: "Ovaj razgovor je završen. Možete preuzeti transkript.",
  }
};

const handoffTriggerKeywords = [
  "real agent", "live agent", "human support", "speak to a person", "live support", "talk to someone",
  "agjent real", "fol me agjent", "njeri real", "dua te flas me dike", "suport direkt", "operator",
  "pravi agent", "ljudska podrska", "razgovor sa osobom", "ljudski operater", "ziva podrska",
];

// --- Animation Variants ---
const modalVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.95 },
  visible: { opacity: 1, y: 0, scale: 1, transition: { type: "spring", stiffness: 100, damping: 20, duration: 0.4 } },
  exit: { opacity: 0, y: 50, scale: 0.95, transition: { duration: 0.2, ease: "easeIn" } },
};
const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1 },
  exit: { opacity: 0 },
};
const messageVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" } },
};
const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.2 } },
};

export function ModernAiModal({ isOpen, onClose, currentUserId, lang = 'en' }) {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [chatState, setChatState] = useState('bot');
  const [isBotTyping, setIsBotTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const socketRef = useRef(null);
  const langConfig = languages[lang] || languages['en'];

  useEffect(() => {
    if (isOpen && currentUserId) {
      setMessages([{ type: "assistant", text: langConfig.initialMessage }]);
      setChatState('bot');
      setInputText('');
      setIsBotTyping(false);

      const socket = io(SOCKET_SERVER_URL, { transports: ["websocket"] });
      socketRef.current = socket;
      socket.on("connect", () => socket.emit('join-session', { sessionId: currentUserId, role: 'user' }));
      socket.on("agent-linked", ({ agent }) => {
        setMessages((prev) => [...prev, { type: "system", text: langConfig.agentJoined(agent.name) }]);
        setChatState('agent');
      });
      socket.on("agent-reply", ({ message }) => setMessages((prev) => [...prev, { type: "agent", text: message }]));
      socket.on("chat-ended-by-agent", () => {
        setMessages(prev => [...prev, { type: 'system', text: langConfig.chatEndedByAgent }]);
        setChatState('ended');
      });
      return () => socket.disconnect();
    }
  }, [isOpen, currentUserId, lang]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isBotTyping]);

  const handleClose = () => onClose();

  const handleSend = async () => {
    if (!inputText.trim() || chatState === 'waiting' || chatState === 'ended' || isBotTyping) return;
    const currentInput = inputText;
    setMessages((prev) => [...prev, { type: "user", text: currentInput }]);
    setInputText("");

    if (chatState === 'agent') {
      socketRef.current?.emit("forward-to-agent", { userId: currentUserId, message: currentInput });
      return;
    }

    if (chatState === 'bot') {
      const wantsAgent = handoffTriggerKeywords.some(k => currentInput.toLowerCase().includes(k));
      if (wantsAgent) {
        setChatState('waiting');
        setMessages((prev) => [...prev, { type: "system", text: langConfig.connecting }]);
        socketRef.current?.emit("agent-request", { userId: currentUserId, message: currentInput, timestamp: new Date().toISOString() });
      } else {
        setIsBotTyping(true);
        try {
          const res = await fetch(CHAT_API_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ message: currentInput, language: lang }),
          });
          const data = await res.json();
          setMessages((prev) => [...prev, { type: "assistant", text: data.reply }]);
        } catch (err) {
          setMessages((prev) => [...prev, { type: "system", text: "Sorry, I have a connection issue." }]);
        } finally {
          setIsBotTyping(false);
        }
      }
    }
  };

  const handleEndChat = () => {
    setMessages(prev => [...prev, { type: 'system', text: langConfig.chatEnded }]);
    setChatState('ended');
    socketRef.current?.emit('user-ended-chat', { userId: currentUserId });
  };
  
  const handleDownloadTranscript = () => {
    const transcript = messages.map(msg => {
      const prefix = msg.type === 'user' ? 'You' : (msg.type === 'agent' ? 'Support' : (msg.type === 'system' ? 'System' : 'Void Assistant'));
      return `${prefix}: ${msg.text}`;
    }).join('\n');
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-transcript-${currentUserId}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isInputDisabled = chatState === 'waiting' || chatState === 'ended' || isBotTyping;
  let placeholderText = langConfig.placeholder;
  if (chatState === 'waiting') placeholderText = langConfig.placeholderWaiting;
  if (chatState === 'ended') placeholderText = langConfig.placeholderEnded;

  const getAvatar = (type) => {
    const avatarClass = "w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0";
    switch (type) {
      case 'user': return <div className={`${avatarClass} bg-[#16a085] text-white`}><FaUser size={16} /></div>;
      case 'assistant': return <div className={`${avatarClass} bg-slate-700 p-1`}><img src="/images/void.png" alt="Void AI" /></div>;
      case 'agent': return <div className={`${avatarClass} bg-sky-800 text-white`}><FaHeadset size={16} /></div>;
      default: return null;
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 flex items-end justify-end p-4 sm:p-6 z-[9999]">
          <motion.div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm" onClick={handleClose} variants={backdropVariants} initial="hidden" animate="visible" exit="exit" />
          <motion.div className="relative w-full max-w-lg bg-[#0F172A] rounded-2xl shadow-2xl transform border border-[#1E293B] flex flex-col z-50" style={{ height: 'calc(100vh - 4rem)', maxHeight: '700px' }} variants={modalVariants} initial="hidden" animate="visible" exit="exit">
            
            <div className="flex items-center justify-between p-4 border-b border-[#1E293B] flex-shrink-0">
              <div className="flex items-center gap-3">
                <div>
                  <img src="/images/void.png" alt="Void Logo" className="h-8" />
                  <p className="text-[#94A3B8] text-xs mt-1">Powered by Shigjeta LLC</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {chatState === 'ended' && <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={handleDownloadTranscript} title="Download Transcript" className="text-[#94A3B8] hover:text-white transition-colors p-2"><FaDownload /></motion.button>}
                {chatState === 'agent' && <motion.button whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={handleEndChat} title="End Chat" className="text-[#16a085] hover:text-[#1abc9c] transition-colors p-2"><FaTimesCircle /></motion.button>}
                <motion.button whileHover={{ scale: 1.1, rotate: 90 }} whileTap={{ scale: 0.9 }} onClick={handleClose} className="text-[#94A3B8] hover:text-white transition-colors bg-[#1E293B] hover:bg-[#334155] rounded-full p-2"><svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg></motion.button>
              </div>
            </div>

            <motion.div className="flex-grow overflow-y-auto p-4 space-y-5" variants={staggerContainer} initial="hidden" animate="visible">
              {messages.map((message, index) => (
                <motion.div key={index} variants={messageVariants} className={`flex items-end gap-3 ${message.type === "user" ? "justify-end" : "justify-start"}`}>
                  {message.type !== "user" && getAvatar(message.type)}
                  <div className={`w-fit max-w-[80%] px-4 py-3 rounded-2xl ${
                      message.type === "user" ? "bg-[#16a085] text-white rounded-br-none" :
                      message.type === "system" ? "bg-transparent text-slate-400 text-center w-full max-w-full text-xs" :
                      message.type === "agent" ? "bg-sky-800 text-white rounded-bl-none" :
                      "bg-[#1E293B] text-gray-200 rounded-bl-none"
                    }`}>
                    <p className="text-sm whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: message.text }}></p>
                  </div>
                  {message.type === "user" && getAvatar(message.type)}
                </motion.div>
              ))}
              
              {isBotTyping && (
                  <motion.div variants={messageVariants} className="flex items-end gap-3 justify-start">
                      {getAvatar('assistant')}
                      <div className="bg-[#1E293B] rounded-2xl rounded-bl-none p-4 flex items-center gap-2">
                          <div className="h-2 w-2 bg-[#16a085] rounded-full animate-pulse-dot"></div>
                          <div className="h-2 w-2 bg-[#16a085] rounded-full animate-pulse-dot [animation-delay:0.2s]"></div>
                          <div className="h-2 w-2 bg-[#16a085] rounded-full animate-pulse-dot [animation-delay:0.4s]"></div>
                      </div>
                  </motion.div>
              )}
              {chatState === 'waiting' && (<div className="flex justify-center"><div className="bg-[#1E293B]/50 text-slate-400 text-xs px-3 py-1 rounded-full">{langConfig.connecting}</div></div>)}

              <div ref={messagesEndRef} />
            </motion.div>

            <div className="p-4 border-t border-[#1E293B] flex-shrink-0 relative">
              <textarea value={inputText} onChange={(e) => setInputText(e.target.value)} onKeyDown={handleKeyPress} className="w-full p-4 pr-16 bg-[#1E293B] text-white rounded-xl focus:ring-2 focus:ring-[#16a085] outline-none resize-none placeholder-[#94A3B8] disabled:opacity-50 transition-opacity" placeholder={placeholderText} rows={1} style={{ maxHeight: "120px" }} disabled={isInputDisabled} />
              <div className="absolute right-6 top-1/2 -translate-y-1/2 flex items-center">
                <motion.button onClick={handleSend} disabled={isInputDisabled || !inputText.trim()} aria-label="Send message" className={`p-2 rounded-full transition-all duration-300 ${!isInputDisabled && inputText.trim() ? "bg-[#16a085] text-white" : "bg-transparent text-[#94A3B8] cursor-not-allowed"}`} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}>
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" /></svg>
                </motion.button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export default ModernAiModal;